import {
  AuditedProduct,
  Grade,
  GradeType,
  RawScraperRow,
  SCORING_CONFIG,
  ScoreItem,
  ScoreStatus,
  ScoreStatusType,
  VIDEO_SCORING_ENABLED
} from './scoring-engine-constants';

/**
 * Robust, self-contained CSV parser capable of parsing double-quoted lines,
 * escaped quotes, and embedded line breaks inside description/bullets cells.
 */
export const parseCSVString = (rawCsvText: string): string[][] => {
  // Strip a UTF-8 BOM so it can't corrupt the first header cell.
  const csvText = rawCsvText.charCodeAt(0) === 0xfeff ? rawCsvText.slice(1) : rawCsvText;
  const result: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;

  for (let i = 0; i < csvText.length; i++) {
    const char = csvText[i];
    const nextChar = csvText[i + 1];

    if (inQuotes) {
      if (char === '"') {
        if (nextChar === '"') {
          cell += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        cell += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === ',') {
      row.push(cell);
      cell = '';
    } else if (char === '\r' || char === '\n') {
      row.push(cell);
      cell = '';
      if (row.length > 0 && row.some(c => c.trim() !== '')) {
        result.push(row);
      }
      row = [];
      if (char === '\r' && nextChar === '\n') {
        i++;
      }
    } else {
      cell += char;
    }
  }

  if (cell !== '' || row.length > 0) {
    row.push(cell);
    if (row.some(c => c.trim() !== '')) {
      result.push(row);
    }
  }

  return result;
};

export const parseBooleanDefensively = (val: unknown): boolean => {
  if (val === undefined || val === null) return false;
  if (typeof val === 'boolean') return val;
  const s = String(val).trim().toUpperCase();
  if (s === '' || s === 'NO' || s === 'FALSE' || s === '0') return false;
  return s === 'TRUE' || s === 'YES' || s === '1' || s === 'Y' || s === 'T';
};

export const parseNumberDefensively = (val: unknown, defaultVal = 0): number => {
  if (val === undefined || val === null) return defaultVal;
  if (typeof val === 'number') {
    return Number.isNaN(val) ? defaultVal : val;
  }
  const str = String(val).trim();
  if (str === '') return defaultVal;
  const num = Number(str.replace(/[^0-9.-]/g, ''));
  return Number.isNaN(num) ? defaultVal : num;
};

/** Evaluates a single product row against SCORING_CONFIG's weighted criteria. */
export const auditProductListing = (raw: RawScraperRow, id: string): AuditedProduct => {
  const asin = String(raw.info_asin ?? '').trim();
  const brand = String(raw.brand || '').trim() || ' — ';
  const brandUrl = String(raw.brand_url || '').trim() || ' — ';
  const category = String(raw.product_category || '').trim() || ' — ';
  const group = String(raw.product_group || '').trim() || ' — ';
  const name = String(raw.name || '').trim() || ' — ';
  const fullDescription = String(raw.full_description || '').trim() || ' — ';
  const uri = String(raw.URI || '').trim() || ' — ';
  const pAsin = String(raw.pASIN ?? '').trim() || ' — ';

  const cleanRatingStr = String(raw.Rating ?? '')
    .trim()
    .replace(/[^0-9.]/g, '');
  const ratingVal = parseFloat(cleanRatingStr);
  const rating = Number.isNaN(ratingVal) ? 0 : ratingVal;

  const cleanReviewsStr = String(raw.total_reviews ?? '')
    .trim()
    .replace(/[^0-9]/g, '');
  const totalReviewsVal = parseInt(cleanReviewsStr, 10);
  const totalReviews = Number.isNaN(totalReviewsVal) ? 0 : totalReviewsVal;

  const countOfImages = parseNumberDefensively(raw.count_of_images, 0);

  // Video presence comes from the `video`/`videos_count` columns, not `URI` — `URI`
  // is the PDP link itself (see flattenAmazonProduct in CommerceScraperUtils.ts,
  // which always populates it, falling back to the dp/{asin} link), so using it as
  // a video proxy would mark every live-scraped listing as having a video.
  const videosCountFromCount = parseNumberDefensively(raw.videos_count, -1);
  const rawVideo = String(raw.video || '').trim();
  const isVideoPopulated =
    rawVideo.length > 0 && rawVideo.toUpperCase() !== 'NONE' && rawVideo !== '—';
  let videosCount = 0;
  if (videosCountFromCount >= 0) {
    videosCount = videosCountFromCount;
  } else if (isVideoPopulated) {
    videosCount = 1;
  }

  const aplusPresent = parseBooleanDefensively(raw.aplus_present);
  const climatePledgeFriendly = parseBooleanDefensively(raw.climate_pledge_friendly);
  const amazonsChoiceFlag = parseBooleanDefensively(raw.amazons_choice_flag);
  const bestSellerFlag = parseBooleanDefensively(raw.best_seller_flag);
  const primeFlag = parseBooleanDefensively(raw.prime_flag);

  const variantRaw = String(raw.variant_asins || '').trim();
  const isVariationActive =
    (variantRaw !== '' && variantRaw !== ' — ' && variantRaw !== 'None') ||
    variantRaw.includes('|');
  const variantAsins =
    isVariationActive && variantRaw
      ? variantRaw
          .split('|')
          .map(v => v.trim())
          .filter(v => v !== '')
      : [];

  let featureBullets: string[] = [];
  const bulletsStr = String(raw.feature_bullets || '').trim();
  if (bulletsStr) {
    featureBullets = bulletsStr
      .split('|')
      .map(b => b.trim())
      .filter(b => b !== '');
  }

  const rawCount = parseNumberDefensively(raw.feature_bullets_count, -1);
  const featureBulletsCount =
    rawCount < 0 ? featureBullets.length : Math.max(rawCount, featureBullets.length);

  const cfg = SCORING_CONFIG;
  let warningCount = 0;
  let failCount = 0;

  let bulletsScoreVal = 0;
  let bulletsStatus: ScoreStatusType = ScoreStatus.FAIL;
  if (featureBulletsCount >= cfg.bullets.passMin) {
    bulletsScoreVal = cfg.bullets.weight;
    bulletsStatus = ScoreStatus.PASS;
  } else if (
    featureBulletsCount >= cfg.bullets.warnMin &&
    featureBulletsCount <= cfg.bullets.warnMax
  ) {
    bulletsScoreVal = cfg.bullets.warningPoints;
    bulletsStatus = ScoreStatus.WARNING;
    warningCount++;
  } else {
    failCount++;
  }
  const bulletsScore: ScoreItem = {
    score: bulletsScoreVal,
    maxScore: cfg.bullets.weight,
    actualValue: featureBulletsCount,
    status: bulletsStatus,
    label: `${featureBulletsCount} Bullet${featureBulletsCount === 1 ? '' : 's'}`,
    criteriaText: cfg.bullets.criteriaText
  };

  const titleLen = name === ' — ' || name === '' ? 0 : name.length;
  let titleScoreVal = 0;
  let titleStatus: ScoreStatusType = ScoreStatus.FAIL;
  if (titleLen >= cfg.title.passMin && titleLen <= cfg.title.passMax) {
    titleScoreVal = cfg.title.weight;
    titleStatus = ScoreStatus.PASS;
  } else if (titleLen >= cfg.title.warnLowMin && titleLen <= cfg.title.warnLowMax) {
    titleScoreVal = cfg.title.warningPoints;
    titleStatus = ScoreStatus.WARNING;
    warningCount++;
  } else {
    failCount++;
  }
  const titleScore: ScoreItem = {
    score: titleScoreVal,
    maxScore: cfg.title.weight,
    actualValue: titleLen,
    status: titleStatus,
    label: `${titleLen} Chars`,
    criteriaText: cfg.title.criteriaText
  };

  const descLen = fullDescription === ' — ' || fullDescription === '' ? 0 : fullDescription.length;
  let descScoreVal = 0;
  let descStatus: ScoreStatusType = ScoreStatus.FAIL;
  if (descLen >= cfg.description.passMin) {
    descScoreVal = cfg.description.weight;
    descStatus = ScoreStatus.PASS;
  } else if (descLen >= cfg.description.warnMin && descLen <= cfg.description.warnMax) {
    descScoreVal = cfg.description.warningPoints;
    descStatus = ScoreStatus.WARNING;
    warningCount++;
  } else {
    failCount++;
  }
  const descriptionScore: ScoreItem = {
    score: descScoreVal,
    maxScore: cfg.description.weight,
    actualValue: descLen,
    status: descStatus,
    label: `${descLen} Chars`,
    criteriaText: cfg.description.criteriaText
  };

  let imagesScoreVal = 0;
  let imagesStatus: ScoreStatusType = ScoreStatus.FAIL;
  if (countOfImages >= cfg.images.passMin) {
    imagesScoreVal = cfg.images.weight;
    imagesStatus = ScoreStatus.PASS;
  } else if (countOfImages === cfg.images.warnExact) {
    imagesScoreVal = cfg.images.warningPoints;
    imagesStatus = ScoreStatus.WARNING;
    warningCount++;
  } else {
    failCount++;
  }
  const imagesScore: ScoreItem = {
    score: imagesScoreVal,
    maxScore: cfg.images.weight,
    actualValue: countOfImages,
    status: imagesStatus,
    label: `${countOfImages} Image${countOfImages === 1 ? '' : 's'}`,
    criteriaText: cfg.images.criteriaText
  };

  let ratingScoreVal = 0;
  let ratingStatus: ScoreStatusType = ScoreStatus.FAIL;
  if (rating >= cfg.rating.passMin) {
    ratingScoreVal = cfg.rating.weight;
    ratingStatus = ScoreStatus.PASS;
  } else if (rating >= cfg.rating.warnMin && rating < cfg.rating.passMin) {
    ratingScoreVal = cfg.rating.warningPoints;
    ratingStatus = ScoreStatus.WARNING;
    warningCount++;
  } else {
    failCount++;
  }
  const ratingScore: ScoreItem = {
    score: ratingScoreVal,
    maxScore: cfg.rating.weight,
    actualValue: rating === 0 ? '—' : rating.toFixed(1),
    status: ratingStatus,
    label: rating === 0 ? 'No Rating' : `${rating.toFixed(1)} Stars`,
    criteriaText: cfg.rating.criteriaText
  };

  let reviewsScoreVal = 0;
  let reviewsStatus: ScoreStatusType = ScoreStatus.FAIL;
  if (totalReviews >= cfg.reviews.passMin) {
    reviewsScoreVal = cfg.reviews.weight;
    reviewsStatus = ScoreStatus.PASS;
  } else if (totalReviews >= cfg.reviews.warnMin && totalReviews <= cfg.reviews.warnMax) {
    reviewsScoreVal = cfg.reviews.warningPoints;
    reviewsStatus = ScoreStatus.WARNING;
    warningCount++;
  } else {
    failCount++;
  }
  const reviewsScore: ScoreItem = {
    score: reviewsScoreVal,
    maxScore: cfg.reviews.weight,
    actualValue: totalReviews,
    status: reviewsStatus,
    label: `${totalReviews.toLocaleString()} Review${totalReviews === 1 ? '' : 's'}`,
    criteriaText: cfg.reviews.criteriaText
  };

  // Video scoring is temporarily disabled (VIDEO_SCORING_ENABLED) because the
  // scraper does not supply video data — see the flag comment in
  // scoring-engine-constants.ts. While off, a missing video is NOT counted as a
  // fail and video contributes 0 to both the earned points and the denominator.
  let videosScoreVal = 0;
  let videosStatus: ScoreStatusType = ScoreStatus.FAIL;
  if (videosCount >= 1) {
    videosScoreVal = cfg.videos.weight;
    videosStatus = ScoreStatus.PASS;
  } else if (VIDEO_SCORING_ENABLED) {
    failCount++;
  }
  const videosScore: ScoreItem = {
    score: videosScoreVal,
    maxScore: cfg.videos.weight,
    actualValue: videosCount >= 1 ? 'YES' : 'NO',
    status: videosStatus,
    label: videosCount >= 1 ? 'YES' : 'NO',
    criteriaText: cfg.videos.criteriaText
  };

  let aplusScoreVal = 0;
  let aplusStatus: ScoreStatusType = ScoreStatus.FAIL;
  if (aplusPresent) {
    aplusScoreVal = cfg.aplus.weight;
    aplusStatus = ScoreStatus.PASS;
  } else {
    failCount++;
  }
  const aplusScore: ScoreItem = {
    score: aplusScoreVal,
    maxScore: cfg.aplus.weight,
    actualValue: aplusPresent ? 'Yes' : 'No',
    status: aplusStatus,
    label: aplusPresent ? 'Present' : 'Missing',
    criteriaText: cfg.aplus.criteriaText
  };

  // Earned points, then normalize over the max achievable across the *enabled*
  // criteria. When VIDEO_SCORING_ENABLED is true this is a no-op (max = 100, so
  // the raw sum is returned unchanged); when false, video's weight is dropped
  // from both numerator and denominator so listings aren't penalized 5 pts for
  // data the scraper can't provide.
  const earnedPoints =
    bulletsScoreVal +
    titleScoreVal +
    descScoreVal +
    imagesScoreVal +
    ratingScoreVal +
    reviewsScoreVal +
    aplusScoreVal +
    (VIDEO_SCORING_ENABLED ? videosScoreVal : 0);

  const enabledMaxPoints =
    cfg.bullets.weight +
    cfg.title.weight +
    cfg.description.weight +
    cfg.images.weight +
    cfg.rating.weight +
    cfg.reviews.weight +
    cfg.aplus.weight +
    (VIDEO_SCORING_ENABLED ? cfg.videos.weight : 0);

  const finalScore = Math.round((earnedPoints / enabledMaxPoints) * 100);

  let grade: GradeType = Grade.F;
  if (finalScore >= 90) {
    grade = Grade.A;
  } else if (finalScore >= 80) {
    grade = Grade.B;
  } else if (finalScore >= 70) {
    grade = Grade.C;
  } else if (finalScore >= 60) {
    grade = Grade.D;
  }

  // Alexa for Shopping readiness proxy — 4 measurable structural dimensions:
  // bullets filled, bullets under mobile-truncation length, title in ideal
  // char range, description meeting the completeness floor.
  const ap = cfg.alexaProxy;
  const alexaBulletsFilled =
    featureBulletsCount >= ap.bulletsTarget
      ? ap.dimensionPoints
      : Math.round((featureBulletsCount / ap.bulletsTarget) * ap.dimensionPoints);
  const bulletsUnderMax =
    featureBullets.length === 0
      ? 0
      : featureBullets.filter(b => b.length <= ap.bulletMaxChars).length / featureBullets.length;
  const alexaBulletsLength = Math.round(bulletsUnderMax * ap.dimensionPoints);
  const alexaTitleLen = name.length;
  const alexaTitle =
    alexaTitleLen >= ap.titleMin && alexaTitleLen <= ap.titleIdealMax ? ap.dimensionPoints : 0;
  const alexaDescription =
    fullDescription && fullDescription !== ' — ' && fullDescription.length >= ap.descMin
      ? ap.dimensionPoints
      : 0;
  const alexaReadinessScore =
    alexaBulletsFilled + alexaBulletsLength + alexaTitle + alexaDescription;

  return {
    id,
    asin: asin || `MOCK-${id}`,
    brand,
    brandUrl,
    category,
    group,
    rating,
    totalReviews,
    pAsin,
    variantAsins,
    title: name,
    featureBulletsCount,
    featureBullets,
    fullDescription,
    countOfImages,
    uri,
    aplusPresent,
    climatePledgeFriendly,
    amazonsChoiceFlag,
    bestSellerFlag,
    primeFlag,
    videosCount,
    bulletsScore,
    titleScore,
    descriptionScore,
    imagesScore,
    ratingScore,
    reviewsScore,
    videosScore,
    aplusScore,
    finalScore,
    grade,
    warningCount,
    failCount,
    alexaReadinessScore
  };
};

/** Parses a Blisspoint-format CSV export into scored products. */
export const processBlisspointCSV = (csvContent: string): AuditedProduct[] => {
  const parsedData = parseCSVString(csvContent);
  if (parsedData.length < 2) return [];

  const headers = parsedData[0].map(h => h.trim());
  const rows = parsedData.slice(1);

  const headerIndices: Record<string, number> = {};
  headers.forEach((h, index) => {
    headerIndices[h.toLowerCase().trim()] = index;
  });

  const getColVal = (row: string[], colKeys: string[]): string => {
    for (const key of colKeys) {
      const idx = headerIndices[key.toLowerCase().trim()];
      if (idx !== undefined && row[idx] !== undefined) {
        return row[idx];
      }
    }
    return '';
  };

  return rows
    .map((row, index) => {
      const rawRow: RawScraperRow = {
        info_asin: getColVal(row, ['info_asin', 'asin']),
        brand: getColVal(row, ['brand']),
        brand_url: getColVal(row, [
          'brand_url',
          'brand url',
          'brandurl',
          'brand_store_url',
          'store_url',
          'store url',
          'brand_store_link'
        ]),
        product_category: getColVal(row, ['product_category', 'category']),
        product_group: getColVal(row, ['product_group']),
        Rating: getColVal(row, ['rating']),
        total_reviews: getColVal(row, ['total_reviews']),
        pASIN: getColVal(row, ['pASIN']),
        variant_asins: getColVal(row, ['variant_asins']),
        name: getColVal(row, ['name']),
        feature_bullets_count: getColVal(row, ['feature_bullets_count']),
        feature_bullets: getColVal(row, ['feature_bullets']),
        full_description: getColVal(row, ['full_description']),
        count_of_images: getColVal(row, ['count_of_images']),
        URI: getColVal(row, ['URI']),
        aplus_present: getColVal(row, ['aplus_present']),
        climate_pledge_friendly: getColVal(row, ['climate_pledge_friendly']),
        amazons_choice_flag: getColVal(row, ['amazons_choice_flag']),
        best_seller_flag: getColVal(row, ['best_seller_flag']),
        prime_flag: getColVal(row, ['prime_flag']),
        video: getColVal(row, ['video'])
      };

      if (!rawRow.info_asin && !rawRow.name) {
        return null;
      }

      return auditProductListing(rawRow, `row-${index + 1}-${Math.random().toString(36).slice(2)}`);
    })
    .filter((p): p is AuditedProduct => p !== null);
};

export interface ProjectedRewrites {
  title?: string;
  bullets?: string;
  description?: string;
}

/**
 * Re-runs the scoring engine on a product with one or more fields swapped for
 * AI-rewritten copy. Returns a new AuditedProduct so callers can show
 * "Current 42% → Projected 87%" without mutating the source catalog.
 */
export const projectRewrittenScore = (
  product: AuditedProduct,
  rewrites: ProjectedRewrites
): AuditedProduct => {
  const bulletsStr = rewrites.bullets ?? product.featureBullets.join(' | ');
  const bulletsArr = bulletsStr
    .split('|')
    .map(b => b.trim())
    .filter(b => b !== '');

  return auditProductListing(
    {
      info_asin: product.asin,
      brand: product.brand === ' — ' ? '' : product.brand,
      brand_url: product.brandUrl === ' — ' ? '' : product.brandUrl,
      product_category: product.category === ' — ' ? '' : product.category,
      product_group: product.group === ' — ' ? '' : product.group,
      Rating: product.rating || '',
      total_reviews: product.totalReviews,
      pASIN: product.pAsin === ' — ' ? '' : product.pAsin,
      variant_asins: product.variantAsins.join('|'),
      name: rewrites.title ?? (product.title === ' — ' ? '' : product.title),
      feature_bullets_count: bulletsArr.length,
      feature_bullets: bulletsArr.join('|'),
      full_description:
        rewrites.description ?? (product.fullDescription === ' — ' ? '' : product.fullDescription),
      count_of_images: product.countOfImages,
      URI: product.uri === ' — ' ? '' : product.uri,
      aplus_present: product.aplusPresent,
      climate_pledge_friendly: product.climatePledgeFriendly,
      amazons_choice_flag: product.amazonsChoiceFlag,
      best_seller_flag: product.bestSellerFlag,
      prime_flag: product.primeFlag
    },
    product.id
  );
};
